import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ClipboardPaste, IdCard, MessageSquare, Search, List as ListIcon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { HEAD, Pill } from "@/components/PdPill";
import { AgencyEkycChat } from "@/components/AgencyEkycChat";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" }) : "";

const TypeTag = ({ t }) => (
  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${t === "aadhaar" ? "bg-[#8A6046]/15 text-[#8A6046]" : "bg-[#385C4B]/12 text-[#385C4B]"}`}>
    {t === "aadhaar" ? "Aadhaar" : "LPG ID"}
  </span>
);

const RecordList = ({ records, counts }) => (
  <>
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#6B6B61] mb-3" data-testid="ekyc-counts">
      <span>Pending <b className="text-[#C8553D] tabular-nums">{counts.pending}</b></span>
      <span>Done <b className="text-emerald-700 tabular-nums">{counts.done}</b></span>
      <span>Total <b className="text-[#1C1C19] tabular-nums">{counts.total}</b></span>
    </div>
    <div className="divide-y divide-[#F0EFE8] border border-[#E0DFD6] rounded-xl overflow-hidden bg-white">
      {records.map((r) => (
        <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#FAFAF6] transition-colors" data-testid={`ekyc-rec-${r.id}`}>
          <IdCard className="w-4 h-4 text-[#6B6B61] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-semibold text-[#1C1C19] text-sm">{r.ident}</span>
              <TypeTag t={r.ident_type} />
              {r.status === "done" && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Done</span>}
            </div>
            {(r.name || r.mobile) && (
              <div className="text-xs text-[#6B6B61] truncate">
                {r.name}{r.name && r.mobile ? " · " : ""}{r.mobile}
              </div>
            )}
          </div>
          <span className="text-[10px] text-[#9A9A90] whitespace-nowrap">{fmtDate(r.created_at)}</span>
        </div>
      ))}
      {!records.length && <div className="py-12 text-center text-sm text-[#9A9A90]" data-testid="ekyc-empty">Koi EKYC entry nahi.</div>}
    </div>
  </>
);

export default function AgencyEkyc() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [view, setView] = useState("chat"); // "chat" | "list"
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ records: [], counts: { pending: 0, done: 0, total: 0 } });
  const [overview, setOverview] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [chatGroupId, setChatGroupId] = useState("");

  const loadMember = useCallback(() => {
    setLoading(true);
    api.get("/agency-ekyc/my", { params: { status, q } })
      .then(({ data: d }) => { setData({ records: d.records, counts: d.counts }); setChatGroupId(d.chat_group_id || ""); })
      .finally(() => setLoading(false));
  }, [status, q]);

  const loadOverview = useCallback(() => {
    api.get("/agency-ekyc/overview").then(({ data }) => setOverview(data.rows)).catch(() => {});
  }, []);

  const loadGroup = useCallback(() => {
    if (!groupId) return;
    setLoading(true);
    api.get(`/agency-ekyc/group/${groupId}`, { params: { status, q } })
      .then(({ data: d }) => setData({ records: d.records, counts: d.counts }))
      .finally(() => setLoading(false));
  }, [groupId, status, q]);

  useEffect(() => {
    if (!isAdmin) loadMember();
    else if (groupId) loadGroup();
    else { loadOverview(); setLoading(false); }
  }, [isAdmin, groupId, loadMember, loadGroup, loadOverview]);

  const filteredOverview = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? overview.filter((r) => r.group_name.toLowerCase().includes(s)) : overview;
  }, [overview, q]);

  const inGroup = !isAdmin || !!groupId;           // a specific agency is open
  const activeChatGroup = isAdmin ? groupId : chatGroupId;

  return (
    <div className="min-h-screen bg-[#F4F3EC]" data-testid="agency-ekyc-page">
      <header className="bg-[#385C4B] text-white sticky top-0 z-30 shadow-[0_6px_20px_rgba(56,92,75,0.2)]">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2.5">
          <button onClick={() => (isAdmin && groupId ? setGroupId("") : navigate(-1))} className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors" data-testid="ekyc-back" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <IdCard className="w-5 h-5" />
          <h1 className={`${HEAD} text-lg sm:text-xl font-semibold`}>EKYC</h1>
          {isAdmin && (
            <button onClick={() => navigate("/ekyc/paste")} data-testid="ekyc-goto-paste"
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-full">
              <ClipboardPaste className="w-3.5 h-3.5" /> Bulk Paste
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-6 py-5 space-y-4">
        {inGroup && (
          <div className="flex items-center gap-2" data-testid="ekyc-view-toggle">
            <Pill on={view === "chat"} onClick={() => setView("chat")} testid="ekyc-view-chat">
              <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Chat</span>
            </Pill>
            <Pill on={view === "list"} onClick={() => setView("list")} testid="ekyc-view-list">
              <span className="inline-flex items-center gap-1.5"><ListIcon className="w-3.5 h-3.5" /> Consumers</span>
            </Pill>
          </div>
        )}

        {inGroup && view === "chat" ? (
          activeChatGroup ? <AgencyEkycChat groupId={activeChatGroup} />
            : <div className="py-16 text-center text-sm text-[#9A9A90]" data-testid="ekyc-chat-none">Koi active agency group nahi mila.</div>
        ) : (
          <>
            {inGroup && (
              <div className="flex flex-wrap items-center gap-2">
                <Pill on={status === "pending"} onClick={() => setStatus("pending")} testid="ekyc-tab-pending">Pending</Pill>
                <Pill on={status === "done"} onClick={() => setStatus("done")} testid="ekyc-tab-done">Done</Pill>
                <Pill on={status === "all"} onClick={() => setStatus("all")} testid="ekyc-tab-all">All</Pill>
                <label className="relative ml-auto">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A9A90]" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ID / name / mobile"
                    className="h-9 pl-8 pr-3 w-52 rounded-full border border-[#E0DFD6] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30" data-testid="ekyc-search" />
                </label>
              </div>
            )}
            {!inGroup && (
              <label className="relative block">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A9A90]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Agency search"
                  className="h-9 pl-8 pr-3 w-full sm:w-72 rounded-full border border-[#E0DFD6] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30" data-testid="ekyc-search" />
              </label>
            )}
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" /></div>
            ) : isAdmin && !groupId ? (
              <div className="divide-y divide-[#F0EFE8] border border-[#E0DFD6] rounded-xl overflow-hidden bg-white" data-testid="ekyc-overview">
                {filteredOverview.map((r) => (
                  <button key={r.group_id} onClick={() => setGroupId(r.group_id)} data-testid={`ekyc-group-${r.group_id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FAFAF6] transition-colors">
                    <span className="min-w-0 flex-1 font-medium text-[#1C1C19] truncate">{r.group_name}</span>
                    <span className="text-xs text-[#C8553D] tabular-nums">{r.pending} pending</span>
                    <span className="text-xs text-[#9A9A90] tabular-nums">/ {r.total}</span>
                  </button>
                ))}
                {!filteredOverview.length && <div className="py-12 text-center text-sm text-[#9A9A90]">Abhi koi EKYC data nahi. Bulk Paste se add karo.</div>}
              </div>
            ) : (
              <RecordList records={data.records} counts={data.counts} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
